import React, { useEffect, useState } from 'react';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { useUserContext } from '../../../../context/user_context/UserContext';
import BaseModal from '../../../../components/modals/BaseModal';
import { useOrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import { Button, TextField, FormControl, InputLabel, Select, MenuItem, InputAdornment } from '@mui/material';
import { updateAsset } from '../../../../repository/AssetDAO';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Timestamp } from 'firebase/firestore';
import { ToastType, useToast } from '../../../../context/toast/ToastContext';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useAssetConditions } from './AssetConditions';

const EditAssetModal = ({ show, onHide, asset }) => {
    const { setLoadingData } = useLoadingContext();
    const [serialNumber, setSerialNumber] = useState("");
    const [assetName, setAssetName] = useState("");
    const [condition, setCondition] = useState("");
    const [price, setPrice] = useState("");
    const [employeeId, setEmployeeId] = useState("");
    const [purchaseDate, setPurchaseDate] = useState(null);
    const [assetNameError, setAssetNameError] = useState(false);
    const { firebaseUser } = useUserContext();
    const { organizationEmployees } = useOrganizationContext();
    const { showToast } = useToast();
    const { t } = useTranslation();
    const { getTranslatedConditions, getConditionKey, getTranslatedValue } = useAssetConditions();

    useEffect(() => {
        if (asset) {
            setSerialNumber(asset.serial_number);
            setAssetName(asset.name);
            setCondition(asset.condition);
            setEmployeeId(asset.employee_id);
            if (asset.price) setPrice(asset.price);
            if (asset.purchase_date != null)
                setPurchaseDate(dayjs(new Date(asset.purchase_date.seconds * 1000)));
        }
    }, [asset, show]);

    useEffect(() => {
        if (!show) resetForm();
    }, [show]);

    const handleSubmit = async () => {
        if (!assetName.trim()) {
            setAssetNameError(true);
            return;
        }
    
        setLoadingData(true);
        const updatedAsset = {
            serial_number: serialNumber,
            name: assetName,
            condition: condition, 
            employee_id: employeeId,
            price: price,
            purchase_date: purchaseDate ? Timestamp.fromDate(new Date(purchaseDate.$d)) : null,
        };
    
        try {
            const result = await updateAsset(firebaseUser.organization, asset.id, updatedAsset);
            
            if (result.success) {
                showToast(t(StringKeys.SUCCESSFULLY_UPDATED_X, { x: assetName }), ToastType.SUCCESS);
                resetForm();
            } else {
                showToast(t(StringKeys.FAILED_TO_UPDATE_X, { x: assetName }), ToastType.DANGER);
            }
        } catch (error) {
            showToast(t(StringKeys.FAILED_TO_UPDATE_X, { x: assetName }), ToastType.DANGER);
        } finally {
            setLoadingData(false);
            onHide();
        }
    };

    const resetForm = () => {
        setSerialNumber("");
        setAssetName("");
        setCondition("");
        setEmployeeId("");
        setPrice("");
        setPurchaseDate(null);
        setAssetNameError(false);
    };

    const actions = (
        <Button onClick={handleSubmit}>
            {t(StringKeys.SUBMIT)}
        </Button>
    );

    if (!asset) return null;

    return (
        <BaseModal
            open={show}
            onClose={() => {
                resetForm();
                onHide();
            }}
            title={t(StringKeys.EDIT_X, {x:`${assetName}`})}
            actions={actions}
        >
            <TextField
                autoFocus
                margin="dense"
                id="name"
                label={t(StringKeys.ASSET_NAME)}
                type="text"
                fullWidth
                variant="outlined"
                value={assetName}
                onChange={(e) => {
                    setAssetName(e.target.value);
                    if (assetNameError) setAssetNameError(false);
                }}
                error={assetNameError}
                helperText={assetNameError ? t(StringKeys.ASSET_NAME_REQUIRED) : ""}
            />
            <TextField
                margin="dense"
                id="serial-number"
                label={t(StringKeys.SERIAL_NUMBER)}
                type="text"
                fullWidth
                variant="outlined"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
            />
            <div style={{ marginTop: "5px" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label={t(StringKeys.PURCHASE_DATE)}
                        value={purchaseDate}
                        onChange={(newValue) => setPurchaseDate(newValue)}
                        renderInput={(params) => <TextField {...params} />}
                        sx={{ width: "100% " }}
                        className="MuiDayCalendar-header MuiDayCalendar-weekContainer"
                    />
                </LocalizationProvider>
            </div>
            <FormControl fullWidth margin="dense">
                <InputLabel id="condition-label">{t(StringKeys.CONDITION)}</InputLabel>
                <Select
                    labelId="condition-label"
                    id="condition-select"
                    value={getTranslatedValue(condition)} // Display translated value
                    label={t(StringKeys.CONDITION)}
                    onChange={(e) => setCondition(getConditionKey(e.target.value))} // Save English key
                >
                    {getTranslatedConditions().map(({ key, value }) => (
                        <MenuItem key={key} value={value}>
                            {value}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
                <InputLabel id="employee-label">{t(StringKeys.EMPLOYEE_IN_POSSESSION)}</InputLabel>
                <Select
                    labelId="employee-label"
                    id="employee-select"
                    value={employeeId}
                    label={t(StringKeys.EMPLOYEE_IN_POSSESSION)}
                    onChange={(e) => setEmployeeId(e.target.value)}
                >
                    <MenuItem value="">
                        <em>{t(StringKeys.NONE)}</em>
                    </MenuItem>
                    {organizationEmployees.map((employee) => (
                        <MenuItem key={employee.id} value={employee.id}>
                            {employee.first_name} {employee.last_name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <TextField
                margin="dense"
                id="price"
                label={t(StringKeys.COST)}
                type="number"
                InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: {
                        min: "0",
                        step: ".01",
                    },
                }}
                fullWidth
                variant="outlined"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
            />
        </BaseModal>
    );
};

export default EditAssetModal;
